import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  ApiTransactionAPIModelFlowDirectionEnum as TransactionAPIModelFlowDirectionEnum,
  ApiTransactionAPIModelStatusEnum as TransactionAPIModelStatusEnum,
} from '@/generated/api/fps/core';
import { makeSelectBank } from '@/model/dictionary/selectors';
import { makeSelectTransaction } from '@/model/wallets/selectors';
import { TransactionProps } from '@/pages/withdrawal/pages/StatusPage/components/SuccessView/types';

export default function useTransaction(
  transactionId: number,
): { transaction?: TransactionProps; status?: TransactionAPIModelStatusEnum } {
  const foundTransaction = useSelector(makeSelectTransaction(transactionId));

  const bankId = useMemo(() => foundTransaction?.sbpInfo?.bankId || foundTransaction?.cardInfo?.bankId, [
    foundTransaction,
  ]);
  const possibleBank = useSelector(makeSelectBank(bankId || '_'));
  const bank = useMemo(() => (bankId ? possibleBank || { id: bankId, label: bankId } : undefined), [
    bankId,
    possibleBank,
  ]);
  const isCredit = foundTransaction?.flowDirection === TransactionAPIModelFlowDirectionEnum.Credit;
  const transaction: TransactionProps | undefined = useMemo(
    () =>
      foundTransaction
        ? {
            isCredit,
            isFPS: !!foundTransaction.sbpInfo,
            id: foundTransaction.transactionId ? `${foundTransaction.transactionId}` : undefined,
            sbpId: foundTransaction.sbpInfo?.id,
            fee: foundTransaction.accountFromFee,
            person: {
              phone: foundTransaction.sbpInfo?.senderPhone,
              name: foundTransaction.sbpInfo?.senderInfo,
            },
            message: foundTransaction.comment,
            createdAt: foundTransaction.timestamp,
            amount: foundTransaction.amount,
            wallet: foundTransaction.account,
            bank,
          }
        : undefined,
    [bank, foundTransaction, isCredit],
  );
  const status = foundTransaction?.status;
  return useMemo(() => ({ transaction, status }), [status, transaction]);
}
